import _defaultTo from 'lodash/defaultTo';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _orderBy from 'lodash/orderBy';
import _pick from 'lodash/pick';
// Serialization
import { deserialize, serialize } from 'serializr';
import {
  carparkSchema,
  carparkPublicSchema,
  doorSchema,
  carparkSettingsOnDemandSchema,
  carparkSettingsLicensePlateSchema,
  carparkSettingsBookingSchema,
  carparkSettingsGeneralSchema,
  carparkSettingsSpacesSchema,
  updateCarparkFinancialInformationInputSchema,
} from './schemas';
// Permissions
import permissions from '../../constants/Permissions';
// Colors
import { carparkSpacesTypePalette } from '../../components/core/colors';
// Fragments
import {
  carparkDefaultSlugFragment,
  carparkFragment,
  carparkForBookingFragment,
  carparkForServiceFeeListFragment,
  carparkSynchronizationFragment,
  operationFragment,
  carparkForListFragment,
  carparkActionBarInformationFragment,
} from './fragments';
// Models
import mDoor from '../door';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';
import { findInList } from '../../utils/Tools';

// Permissions used in model
const {
  pCpm: {
    connectedEquipmentRead, configurationRead, configurationSensitiveRead, licensePlateDelaySettingsRead,
  },
  pBooking: { bookings },
  pFeature,
} = permissions;

/**
 * Serialization
 */
const deserializeCarpark = (data) => deserialize(carparkSchema, data);
const deserializeCarparkPublic = (data) => deserialize(carparkPublicSchema, data);

const serializeCarpark = ({ contact }) => ({
  ...serialize(carparkSchema, {
    ...(contact && { contact }),
  }),
});

const deserializeCarparkDoor = (data) => deserialize(doorSchema, data);

const serializeCarparkSettingsBooking = ({
  capacityCheck,
  entryCourtesyDelay,
  exitCourtesyDelay,
  resetDelay,
  maxDate,
  maxDuration,
  moduleEnabled,
}) => ({
  ...serialize(carparkSettingsBookingSchema, {
    ...(!_isNil(capacityCheck) && { capacityCheck }),
    ...(!_isNil(entryCourtesyDelay) && { entryCourtesyDelay }),
    ...(!_isNil(exitCourtesyDelay) && { exitCourtesyDelay }),
    ...(!_isNil(resetDelay) && { resetDelay }),
    ...(!_isNil(maxDate) && { maxDate }),
    ...(!_isNil(maxDuration) && { maxDuration }),
    moduleEnabled: _defaultTo(moduleEnabled, null),
  }),
});

const serializeCarparkSettingsGeneral = ({
  address,
  email,
  forbidManualKeypadCodes,
  location,
  phone,

  maximumHeight,
  accessSchedule,
  authorizedVehicles,
  allowLpg,
  chargingStation,
}) => ({
  ...serialize(carparkSettingsGeneralSchema, {
    ...(!_isNil(address) && { address }),
    ...(!_isNil(email) && { email }),
    forbidManualKeypadCodes: _defaultTo(forbidManualKeypadCodes, null),
    ...(!_isNil(location) && { location }),
    ...(!_isNil(phone) && { phone }),
    ...(!_isNil(maximumHeight) && { maximumHeight }),
    ...(!_isNil(accessSchedule) && { accessSchedule }),
    ...(!_isNil(authorizedVehicles) && { authorizedVehicles }),
    allowLpg: _defaultTo(allowLpg, null),
    chargingStation: _defaultTo(chargingStation, null),
  }),
});

const serializeCarparkSettingsLicensePlate = ({
  recordCooldownEntryAfterEntry,
  recordCooldownExitAfterExit,
  recordCooldownExitAfterEntry,
  recordCooldownEntryAfterExit,
}) => ({
  ...serialize(carparkSettingsLicensePlateSchema, {
    ...(!_isNil(recordCooldownEntryAfterEntry) && { recordCooldownEntryAfterEntry }),
    ...(!_isNil(recordCooldownExitAfterExit) && { recordCooldownExitAfterExit }),
    ...(!_isNil(recordCooldownExitAfterEntry) && { recordCooldownExitAfterEntry }),
    ...(!_isNil(recordCooldownEntryAfterExit) && { recordCooldownEntryAfterExit }),
  }),
});

const serializeCarparkSettingsOnDemand = ({
  confirmationKeypadCode,
  enabled,
  capacityCheck,
  temporaryExitMaxDuration,
  exitCourtesyDelay,
  dropOffDuration,
  temporaryExitResetDelay,
  ignoredLicensePlates,
  mode,
  settings,
}) => ({
  ...serialize(carparkSettingsOnDemandSchema, {
    ...(!_isNil(confirmationKeypadCode) && { confirmationKeypadCode }),
    enabled: _defaultTo(enabled, null),
    ...(!_isNil(capacityCheck) && { capacityCheck }),
    ...(!_isNil(temporaryExitMaxDuration) && { temporaryExitMaxDuration }),
    ...(!_isNil(exitCourtesyDelay) && { exitCourtesyDelay }),
    ...(!_isNil(dropOffDuration) && { dropOffDuration }),
    ...(!_isNil(temporaryExitResetDelay) && { temporaryExitResetDelay }),
    ...(!_isNil(ignoredLicensePlates) && { ignoredLicensePlates }),
    ...(mode && { mode }),
    ...(settings
      && mode === 'auto_categorization' && {
      autoCategorizationConfiguration: {
        parkingCategoryId: _defaultTo(settings.parkingCategoryId, null),
      },
    }),
    ...(settings
      && mode === 'specific_category' && {
      specificCategoryConfiguration: {
        quickParkingCategoryId: _defaultTo(settings.quickParkingCategoryId, null),
      },
    }),
  }),
});

const serializeCarparkSettingsSpaces = ({ bookingCapacity, assignedCapacity }) => ({
  ...serialize(carparkSettingsSpacesSchema, {
    bookingCapacity: _defaultTo(bookingCapacity, null),
    assignedCapacity: _defaultTo(assignedCapacity, null),
  }),
});

const serializeUpdateFinancialInformation = (data) => serialize(updateCarparkFinancialInformationInputSchema, data);

/**
 * Lists
 */
const carparkSettingsOnDemandModesList = [
  {
    key: 'autoCategorization',
    value: 'auto_categorization',
    labelId: 'carpark.onDemandMode.autoCategorization',
  },
  {
    key: 'specificCategory',
    value: 'specific_category',
    labelId: 'carpark.onDemandMode.specificCategory',
  },
];

const carparkSpacesTypesList = [
  {
    key: 'bookingCapacity',
    value: 'bookingCapacity',
    labelId: 'carpark.spaceType.booking',
    color: carparkSpacesTypePalette.bookings,
    isWithoutBooking: false,
  },
  {
    key: 'assignedCapacity',
    value: 'assignedCapacity',
    labelId: 'carpark.spaceType.assigned',
    color: carparkSpacesTypePalette.assigned,
    isWithoutBooking: false,
  },
  {
    key: 'withoutBookingCapacity',
    value: 'withoutBookingCapacity',
    labelId: 'carpark.spaceType.withoutBooking',
    color: carparkSpacesTypePalette.withoutBooking,
    isWithoutBooking: true,
  },
];

const carparkPaymentFrequencyList = [
  {
    key: 'daily',
    value: 'daily',
    labelId: 'carpark.financialInfo.daily',
  },
  {
    key: 'weekly',
    value: 'weekly',
    labelId: 'carpark.financialInfo.weekly',
  },
  {
    key: 'monthly',
    value: 'monthly',
    labelId: 'carpark.financialInfo.monthly',
  },
];

/**
 * Getters
 */
const carparkGetSettingsOnDemandModeAutoCategorization = carparkSettingsOnDemandModesList[0];
const carparkGetSettingsOnDemandModeSpecificCategory = carparkSettingsOnDemandModesList[1];

const carparkGetSpacesTypeBookingCapacity = carparkSpacesTypesList[0];
const carparkGetSpacesTypeAssignedCapacity = carparkSpacesTypesList[1];
const carparkGetSpacesTypeWithoutBookingCapacity = carparkSpacesTypesList[2];

/**
 * Utils
 */
const carparkTitle = (carparkData) => _get(carparkData, 'name', '');
const carparkPublicName = (carparkData) => _get(carparkData, 'publicName') || carparkTitle(carparkData);

const carparkSortDoors = (doors) => _orderBy(doors, [(door) => mDoor.usage(door).weight, 'name'], ['desc', 'asc']);

const carparkHasSettingsOnDemandSpecificCategory = (carparkData) => _get(carparkData, 'settings.onDemand.mode') === 'specific_category';
const carparkAllowListingOfOneparkBookings = (carparkData) => _get(carparkData, 'allowListingOfOneparkBookings') === true;
const carparkHasSettingsOnDemandEnabled = (carparkData) => _get(carparkData, 'settings.onDemand.enabled') === true;

const carparkSettingsOnDemandMode = (onDemandSettings) => findInList(carparkSettingsOnDemandModesList, 'value', onDemandSettings, 'mode');

const carparkContext = (carparkData) => ({
  ..._pick(carparkData, ['capacity', 'id', 'timezone', 'priceRules', 'settings']),
  ...(carparkData && {
    settingsOnDemandSpecificCategory: carparkHasSettingsOnDemandSpecificCategory(carparkData),
  }),
});

const carparkGetEntryDoors = (carparkData) => {
  const entryDoors = _filter(carparkData.doors, (door) => {
    const equipment = _find(door.equipments, { usage: 'input' });
    return equipment !== undefined;
  });

  return entryDoors;
};

/**
 * GraphQL
 */
const queryPermissions = (hasPermission) => ({
  withEquipmentsRead: hasPermission({ needed: connectedEquipmentRead, permissionsOrContext: 'carparkPermissions' }),
  withConfiguration: hasPermission({ needed: configurationRead, permissionsOrContext: 'carparkPermissions' }),
  withBookings: hasPermission({ needed: bookings, permissionsOrContext: 'carparkPermissions' }),
  withLicensePlateConfiguration: hasPermission({ needed: licensePlateDelaySettingsRead, permissionsOrContext: 'carparkPermissions' }),
  withAdmin: hasPermission({ needed: pFeature.admin }),
  withConfigurationSensitiveRead: hasPermission({ needed: configurationSensitiveRead, permissionsOrContext: 'carparkPermissions' }),
});

const options = {
  workspaceGroupsSearch: `(search:
    {
      and: [
        {
          and: [
            {
              type: {
                eq: CARPARK_DEFAULT
              }
            }
          ]
        },
      ]
    },
  )`,
};

const queListCarparks = `
  query ListCarparks($input: ListCarparksInput, $pagination: PaginationInput, $search: ListCarparksSearchInput, $sort: [ListCarparksSortInput]) {
    listCarparks(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...carparkForListFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${carparkForListFragment}
`;

const queCountCarparks = `
  query CountCarparks($input: ListCarparksInput) {
    listCarparks(input: $input) {
      totalEntries
    }
  }
`;

const queListCarparksForAutoComplete = `
  query ListCarparks($input: ListCarparksInput, $pagination: PaginationInput, $search: ListCarparksSearchInput, $sort: [ListCarparksSortInput]) {
    listCarparks(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries { id, name }
      pageNumber
      pageSize
      totalEntries
      totalPages
    }
  }
`;

const queListCarparksForWorkspaceGroup = `
  query ListCarparks($input: ListCarparksInput, $pagination: PaginationInput, $search: ListCarparksSearchInput, $sort: [ListCarparksSortInput]) {
    listCarparks(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries { ...carparkForServiceFeeListFragment },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${carparkForServiceFeeListFragment}
`;

const queGetCarpark = (hasPermission) => `
  query GetCarpark($input: GetCarparkInput!) {
    getCarpark(input: $input) {
      ...carparkFragment
    }
  }

  ${carparkFragment(queryPermissions(hasPermission), options)}
`;

const queGetCarparkActionBarInformation = `
  query GetCarparkActionBarInformation($input: GetCarparkInput!) {
    getCarpark(input: $input) {
      ...carparkActionBarInformationFragment
    }
  }

  ${carparkActionBarInformationFragment}
`;

const queGetCarparkDefaultSlug = `
  query GetCarpark($input: GetCarparkInput!) {
    getCarpark(input: $input) {
      ...carparkDefaultSlugFragment
    }
  }

  ${carparkDefaultSlugFragment}
`;

const queGetCarparkSynchronizationStatuses = `
  query GetCarpark($input: GetCarparkInput!) {
    getCarpark(input: $input) {
      ...carparkSynchronizationFragment
    }
  }

  ${carparkSynchronizationFragment}
`;

const queGetCarparkForBooking = `
  query GetCarpark($input: GetCarparkInput!) {
    getCarpark(input: $input) {
      ...carparkForBookingFragment
    }
  }

  ${carparkForBookingFragment}
`;

const mutUpdateCarpark = `
  mutation UpdateCarpark($input: UpdateCarparkInput!) {
    updateCarpark(input: $input) {
      id
    }
  }
`;

const mutUpdateCarparkContact = `
  mutation UpdateCarparkContact($input: UpdateCarparkContactInput!) {
    updateCarparkContact(input: $input) {
      id
      contact {
        email
        phone
      }
    }
  }
`;

const mutNotifySupport = `
  query NotifySupport($input: NotifySupportInput!) {
    notifySupport(input: $input)
  }
`;

const mutTriggerDoor = `
  mutation TriggerDoor($input: TriggerDoorInput!) {
    triggerDoor(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const queGetUniqueKeypadCode = `
  query GetUniqueKeypadCode($input: GetUniqueKeypadCodeInput!) {
    getUniqueKeypadCode(input: $input)
  }
`;

const mutUpdateCarparkOnDemandSettings = `
  mutation UpdateCarparkOnDemandSettings($input: CarparkOnDemandSettingsInput!) {
    updateCarparkOnDemandSettings(input: $input) {
      ...operationFragment
    }
  }

  ${operationFragment}
`;

const mutUpdateCarparkLicensePlateSettings = `
  mutation UpdateCarparkLicensePlateSettings($input: CarparkLicensePlateSettingsInput!) {
    updateCarparkLicensePlateSettings(input: $input) {
       id
    }
  }
`;

const mutUpdateCarparkBookingSettings = `
  mutation UpdateCarparkBookingSettings($input: CarparkBookingSettingsInput!) {
    updateCarparkBookingSettings(input: $input) {
      ...operationFragment
    }
  }

  ${operationFragment}
`;

const mutUpdateCarparkGeneralSettings = `
  mutation UpdateCarparkGeneralSettings($input: CarparkGeneralSettingsInput!) {
    updateCarparkGeneralSettings(input: $input) {
      ...operationFragment
    }
  }

  ${operationFragment}
`;

const mutUpdateCarparkPublicName = `
  mutation UpdateCarparkPublicName($input: CarparkPublicNameInput!) {
    updateCarparkPublicName(input: $input) {
      id
      publicName
    }
  }
`;

const mutUpdateCarparkSpacesSettings = `
  mutation UpdateCarparkSpacesSettings($input: CarparkSpacesSettingsInput!) {
    updateCarparkSpacesSettings(input: $input) {
      ...operationFragment
    }
  }

  ${operationFragment}
`;

const mutUpdateCarparkFinancialInformation = `
  mutation UpdateCarparkFinancialInformation($input: UpdateCarparkFinancialInformationInput!) {
    updateCarparkFinancialInformation(input: $input) {
      id
    }
  }
`;

const mutUpdateCarparkPartner = `
  mutation UpdateCarparkPartner($input: UpdateCarparkPartnerInput!) {
    updateCarparkPartner(input: $input) {
      id
    }
  }
`;

const mutUpdateCarparkWorkspaceGroups = `
  mutation UpdateCarparkWorkspaceGroups($input: UpdateCarparkWorkspaceGroupsInput!) {
    updateCarparkWorkspaceGroups(input: $input) {
      id
    }
  }
`;

/**
 * Model
 */
const carpark = {
  // Name and icon
  name: 'carpark',
  icon: 'carpark',

  // Serialization
  deserialize: deserializeCarpark,
  deserializeCarparkPublic,
  serialize: serializeCarpark,
  deserializeDoor: deserializeCarparkDoor,
  serializeSettingsOnDemand: serializeCarparkSettingsOnDemand,
  serializeSettingsSpaces: serializeCarparkSettingsSpaces,
  serializeSettingsBooking: serializeCarparkSettingsBooking,
  serializeSettingsGeneral: serializeCarparkSettingsGeneral,
  serializeSettingsLicensePlate: serializeCarparkSettingsLicensePlate,
  serializeUpdateFinancialInformation,

  // Lists
  settingsOnDemandModesList: carparkSettingsOnDemandModesList,
  spacesTypeList: carparkSpacesTypesList,
  paymentFrequencyList: carparkPaymentFrequencyList,

  // Getters
  getSettingsOnDemandModeAutoCategorization: carparkGetSettingsOnDemandModeAutoCategorization,
  getSettingsOnDemandModeSpecificCategory: carparkGetSettingsOnDemandModeSpecificCategory,

  getSpacesTypeBookingCapacity: carparkGetSpacesTypeBookingCapacity,
  getSpacesTypeAssignedCapacity: carparkGetSpacesTypeAssignedCapacity,
  getSpacesTypeWithoutBookingCapacity: carparkGetSpacesTypeWithoutBookingCapacity,

  // Utils
  title: carparkTitle,
  publicName: carparkPublicName,
  context: carparkContext,
  sortDoors: carparkSortDoors,
  hasSettingsOnDemandSpecificCategory: carparkHasSettingsOnDemandSpecificCategory,
  allowListingOfOneparkBookings: carparkAllowListingOfOneparkBookings,
  hasSettingsOnDemandEnabled: carparkHasSettingsOnDemandEnabled,
  settingsOnDemandMode: carparkSettingsOnDemandMode,
  getEntryDoors: carparkGetEntryDoors,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'name', dir: 'asc' }],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'name',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: () => ({
      xs: 1,
      sm: 1,
      md: 2,
      lg: 2,
      xl: 2,
    }),
    columns: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        sortable: true,
      },
      {
        key: 'address',
        labelId: 'field.address',
        field: 'address',
        sortable: false,
        className: 'hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'name',
        },
      },
      {
        key: 'address',
        labelId: 'field.addressZipcodeCity',
        field: 'address',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'address',
        },
      },
      {
        key: 'country',
        labelId: 'field.country',
        field: 'country',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        withOperators: true,
        filter: {
          field: 'country',
        },
      },
      {
        key: 'workspaceGroupId',
        labelId: '',
        field: 'workspaceGroupId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        excludeFilterFields: ['workspaceGroupId'],
        hidden: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'workspaceGroupId',
        },
      },
      {
        key: 'workspaceId',
        labelId: '',
        field: 'workspaceId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        excludeFilterFields: ['workspaceId'],
        hidden: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'workspaceId',
        },
      },
    ],
  },

  listFeaturesBookModule: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'name', dir: 'asc' }],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'name',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 3,
      lg: 3,
      xl: 3,
    }),
    columns: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        sortable: true,
      },
      {
        key: 'address',
        labelId: 'field.address',
        field: 'address',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'actions',
        field: '',
        sortable: false,
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'name',
        },
      },
      {
        key: 'address',
        labelId: 'field.addressZipcodeCity',
        field: 'address',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'address',
        },
      },
      {
        key: 'country',
        labelId: 'field.country',
        field: 'country',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        withOperators: true,
        filter: {
          field: 'country',
        },
      },
      {
        key: 'workspaceGroupId',
        labelId: '',
        field: 'workspaceGroupId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        excludeFilterFields: ['workspaceGroupId'],
        hidden: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'workspaceGroupId',
        },
      },
      {
        key: 'workspaceId',
        labelId: '',
        field: 'workspaceId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        excludeFilterFields: ['workspaceId'],
        hidden: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'workspaceId',
        },
      },
    ],
  },

  // GraphQL
  queListCarparks,
  queCountCarparks,
  queListCarparksForAutoComplete,
  queGetCarpark,
  queGetCarparkActionBarInformation,
  queGetCarparkDefaultSlug,
  queGetCarparkForBooking,
  queGetCarparkSynchronizationStatuses,
  queListCarparksForWorkspaceGroup,
  mutUpdateCarpark,
  mutUpdateCarparkContact,
  mutNotifySupport,
  mutTriggerDoor,
  queGetUniqueKeypadCode,
  mutUpdateCarparkOnDemandSettings,
  mutUpdateCarparkLicensePlateSettings,
  mutUpdateCarparkGeneralSettings,
  mutUpdateCarparkPublicName,
  mutUpdateCarparkBookingSettings,
  mutUpdateCarparkSpacesSettings,
  mutUpdateCarparkFinancialInformation,
  mutUpdateCarparkPartner,
  mutUpdateCarparkWorkspaceGroups,
};

module.exports = carpark;



// WEBPACK FOOTER //
// ./models/cpm/carpark.js